import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ExternalLink, Calendar } from 'lucide-react';
import { GridPattern, GradientBlob } from '../components/DecorativeElements';
import ImageWithFallback from '../components/ImageWithFallback';
import { preloadImages } from '../utils/imagePreloader';
import './News.css';

const News = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const posts = [
    {
      id: 1,
      title: "Advancing Indo–Sri Lankan Bilateral Trade",
      description: "An enriching dialogue on advancing Indo–Sri Lankan bilateral trade and tourism, with a shared vision for economic integration and sustainable growth. Privileged to engage with Her Excellency Hon. Shirani Ariyarathne, Consul General of Sri Lanka in Mumbai.",
      date: "5 months ago",
      category: "Diplomatic Engagement",
      link: "https://www.linkedin.com/posts/parth-piyush-maheshwari_an-enriching-dialogue-on-advancing-indosri-activity-7348774546981752833-hOKF",
      tags: ["Trade", "Diplomacy", "Sri Lanka"],
      image: "/assets/img/News/Sri Lanka.jfif"
    },
    {
      id: 2,
      title: "Strengthening India-Portugal Bilateral Trade",
      description: "An enriching dialogue on strengthening India Portugal bilateral trade, envisioning a $5 billion USD milestone! Honoured to engage with H.E. Mr. João Ribeiro de Almeida, Ambassador of Portugal to India.",
      date: "6 months ago",
      category: "Trade Mission",
      link: "https://www.linkedin.com/posts/parth-piyush-maheshwari_an-enriching-dialogue-on-strengthening-india-activity-7337902916709593092-dzcO",
      tags: ["Trade", "Portugal", "Investment"],
      image: "/assets/img/News/TRade Mission 1.jfif"
    },
    {
      id: 3,
      title: "SIPBN Trade and Investment Mission to India 2025",
      description: "aAROHAN is glad to be part of the SIPBN Trade and Investment Mission to India 2025! We were privileged to welcome the esteemed delegation from Australia and host an exciting event in collaboration with THub.",
      date: "8 months ago",
      category: "Trade Mission",
      link: "https://www.linkedin.com/posts/aarohan-consulting_sipbn-collaboration-thub-ugcPost-7317864849433063426-PaL8",
      tags: ["Australia", "Startups", "Innovation"],
      image: "/assets/img/News/aArohan.jfif"
    },
    {
      id: 4,
      title: "Speaking at Pillai College: Quantum Computing & Agentic AI",
      description: "It was an absolute pleasure to speak at Pillai College of Engineering during the launch event of their CSI-PCE magazine. I had the opportunity to share insights on two rapidly evolving frontiers: Quantum Computing and Agentic AI.",
      date: "8 months ago",
      category: "Speaking Engagement",
      link: "https://www.linkedin.com/posts/parth-piyush-maheshwari_quantumcomputing-agenticai-pcelaunch-activity-7317820529200054272-P3xU",
      tags: ["Education", "AI", "Quantum Computing"],
      image: "/assets/img/News/Speaking engagement.jfif"
    },
    {
      id: 5,
      title: "India-Australia Trade Mission",
      description: "Strategic partnerships and investment opportunities between Australia and India. Building stronger global connections and nurturing innovation across borders.",
      date: "8 months ago",
      category: "Trade Mission",
      link: "https://www.linkedin.com/posts/parth-piyush-maheshwari_indiaaustralia-trademission-governmentpartnership-activity-7316749179869978624-khPk",
      tags: ["Trade", "Australia", "Partnerships"],
      image: "/assets/img/News/SIPBN Trade Mission Governor.jfif"
    },
    {
      id: 6,
      title: "CSR Initiative: Education & Creativity",
      description: "Committed to social impact through CSR initiatives focused on digital empowerment and education. Fostering creativity and innovation in the next generation.",
      date: "10 months ago",
      category: "CSR",
      link: "https://www.linkedin.com/posts/parth-piyush-maheshwari_csr-education-creativity-activity-7296032098509889537-SMyG",
      tags: ["CSR", "Education", "Social Impact"],
      image: "/assets/img/News/CSR.jfif"
    },
    {
      id: 7,
      title: "Cybersecurity Education Initiative",
      description: "Giving back to the community through cybersecurity education and awareness programs. Empowering the next generation with essential digital security knowledge.",
      date: "1 year ago",
      category: "Education",
      link: "https://www.linkedin.com/posts/parth-piyush-maheshwari_cybersecurity-education-givingback-activity-7206703370953306112-A-oK",
      tags: ["Cybersecurity", "Education", "Community"],
      image: "/assets/img/News/Cyber Security.jfif"
    },
    {
      id: 8,
      title: "Collaboration with Maharashtra Police",
      description: "Working on high-profile cases with Maharashtra Police, contributing expertise in cybersecurity and digital forensics. Teamwork and dedication in action.",
      date: "1 year ago",
      category: "Cybersecurity",
      link: "https://www.linkedin.com/posts/parth-piyush-maheshwari_maharashtrapolice-highprofilecases-teamwork-activity-7073331605162930176-jwPQ",
      tags: ["Cybersecurity", "Law Enforcement", "Digital Forensics"],
      image: "/assets/img/News/Maharashtra Police.jfif"
    },
    {
      id: 9,
      title: "Social Fabrics Initiative",
      description: "Contributing to skill development and social empowerment initiatives. Building stronger communities through education and skill-building programs.",
      date: "2 years ago",
      category: "Social Impact",
      link: "https://www.linkedin.com/posts/parth-piyush-maheshwari_socialfabrics-ssc-skill-activity-6896736551804436481-4Oe2",
      tags: ["Social Impact", "Skills", "Community"],
      image: "/assets/img/News/Social Fabrics.jfif"
    },
    {
      id: 10,
      title: "Student Entrepreneurship",
      description: "Supporting student entrepreneurs and fostering innovation in the education sector. Empowering the next generation of business leaders.",
      date: "2 years ago",
      category: "Education",
      link: "https://www.linkedin.com/posts/parth-piyush-maheshwari_studentpreneur-entrepreneur-business-activity-6891602567059582976-Jhj2",
      tags: ["Entrepreneurship", "Education", "Innovation"],
      image: "/assets/img/News/Student Entrepreneurship.jfif"
    }
  ];

  const categories = ["All", "Trade Mission", "Diplomatic Engagement", "Speaking Engagement", "CSR", "Education", "Cybersecurity", "Social Impact"];

  // Preload first 5 news images immediately (aggressive preloading)
  useEffect(() => {
    const priorityImages = posts.slice(0, 5).map(post => post.image);
    // Load first 3 immediately, then stagger the rest
    priorityImages.slice(0, 3).forEach(src => {
      const img = new Image();
      img.fetchPriority = 'high';
      img.loading = 'eager';
      img.src = src;
    });
    
    // Load remaining 2 with slight delay
    priorityImages.slice(3).forEach((src, index) => {
      setTimeout(() => {
        const img = new Image();
        img.fetchPriority = 'high';
        img.loading = 'eager';
        img.src = src;
      }, (index + 1) * 100);
    });
  }, []);

  return (
    <div className="news-page">
      <section className="news-hero">
        <GridPattern />
        <GradientBlob className="blob-hero" />
        <div className="container">
          <motion.h1
            className="page-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            News & Updates
          </motion.h1>
          <motion.p
            className="page-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Stay updated with our latest engagements, speaking events, trade missions, and social impact initiatives
          </motion.p>
        </div>
      </section>

      <section className="news-content" ref={ref}>
        <div className="container">
          <motion.div
            className="news-grid"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8 }}
          >
            {posts.map((post, index) => (
              <motion.article
                key={post.id}
                className="news-card"
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="news-card-image">
                  <ImageWithFallback
                    src={post.image}
                    alt={post.title}
                    fallback="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop&q=80"
                    priority={index < 3}
                  />
                  <div className="news-card-overlay"></div>
                </div>
                
                <div className="news-card-content">
                  <div className="news-card-header">
                    <span className="news-category">{post.category}</span>
                    <span className="news-date">
                      <Calendar size={14} />
                      {post.date}
                    </span>
                  </div>
                  
                  <h3 className="news-title">{post.title}</h3>
                  
                  <p className="news-description">{post.description}</p>
                  
                  <div className="news-tags">
                    {post.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="news-tag">{tag}</span>
                    ))}
                  </div>
                  
                  <a
                    href={post.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="news-link"
                  >
                    Read on LinkedIn
                    <ExternalLink size={16} />
                  </a>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default News;

